import React, { useMemo, useState, useEffect, useRef } from "react";
import { useSelector } from "react-redux";
import { selectNavItems, selectSpEnvironment } from "state/selectors/Selectors";
import { buildMenuTree, buildMobileTreeMenu } from "../../utils/utils";
import NavigationItem from "./NavigationItem";
import DropdownMenu from "../DropdownMenu";

const Navigation: React.FC = () => {
  const items = useSelector(selectNavItems);
  const navigationItems = useMemo(() => buildMenuTree(items), [items]);
  const mobileNavigationItems = useMemo(() => buildMobileTreeMenu(items), [items]);
  const [openDropdown, setOpenDropDown] = useState<number | null>(null);
  const [idx, setIdx] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);
  const dropdownRef = useRef<HTMLDivElement | null>(null);

  const spEnv = useSelector(selectSpEnvironment);

  // Check screen size on mount and resize
  useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 850);
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  // Close menu when clicking outside
  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setIsMobileMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (openDropdown === null) return;

    function handleClickOutside(e: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpenDropDown(null);
      }
    }

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDropdown]);

  // Set active index based on current path
  useEffect(() => {
    const findActiveIndex = () => {
      const currentPath = window.location.pathname.replace(/\/+$/, "");
      return navigationItems.findIndex(item => {
        try {
          const itemPath = new URL(item.Href, window.location.origin).pathname.replace(/\/+$/, "");
          return currentPath === itemPath;
        } catch {
          return false;
        }
      });
    };

    setIdx(Math.max(0, findActiveIndex()));
  }, [navigationItems]);

  // Render mobile view
  if (isMobile) {
    return (
      <nav className={`relative ${spEnv === "logp" ? "!bg-[#007167]" : "!bg-[#003d4c]"} !w-full`}>
        <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
          <div className="relative flex h-16 items-center justify-between">
            <div className="absolute inset-y-0 left-0 flex items-center sm:hidden">
              <button
                aria-label="Öppna meny"
                type="button"
                onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                className={`focus-visible:outline inline-flex items-center justify-center p-2 !rounded-md text-white ${spEnv === "logp" ? "hover:bg-[#003d4c]" : "hover:bg-[#007167]"} focus:outline-0 border-0`}
                aria-expanded="false"
              >
                <svg className="block h-7 w-7" viewBox="0 0 24 24" stroke="white" aria-hidden="true">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>

            <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
              <div className="flex shrink-0 items-center"></div>

              {/* Mobile menu - shows when open */}
              {isMobileMenuOpen && (
                <div className="absolute top-6 z-10 w-full origin-top-right bg-white rounded-md shadow-lg ring-1 ring-black ring-opacity-5" ref={menuRef}>
                  <DropdownMenu
                    items={mobileNavigationItems}
                    menuClassName={`w-full ${spEnv === "logp" ? "!bg-[#007167]" : "!bg-[#003d4c]"} pt-2 border-2 rounded-md`}
                    itemClassName="block px-4 py-2 text-base text-gray-700 hover:bg-gray-100"
                  />
                </div>
              )}

              {/* Desktop menu - hidden on mobile */}
              <div className="hidden sm:block">
                <div className="flex space-x-4">
                  {mobileNavigationItems.map((item, i) => (
                    <NavigationItem
                      key={item.Title}
                      href={item.Href}
                      itemOrder={item.ItemOrder}
                      selected={i === idx}
                      title={item.Title + 33}
                      index={i}
                      tooltipContent=""
                      onClick={() => setIdx(i)}
                    />
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </nav>
    );
  }

  // Render desktop view
  return (
    <nav className={`relative ${spEnv === "logp" ? "!bg-[#007167]" : "!bg-[#003d4c]"} !w-full`}>
      <div className="mx-auto max-w-7xl px-2 sm:px-6 lg:px-8">
        <div className="relative flex h-16 items-center justify-between">
          <div className="absolute inset-y-0 left-0 flex items-center sm:hidden"></div>
          <div className="flex flex-1 items-center justify-center sm:items-stretch sm:justify-start">
            <div className="flex shrink-0 items-center"></div>
            <div className="sm:ml-6 sm:block">
              <div className="flex space-x-4">
                {navigationItems.map((item, i) => (
                  <div className="relative" ref={i === openDropdown ? dropdownRef : null} key={item.Title}>
                    <NavigationItem
                      isDropdown={item.subMenus?.length > 0 ? true : false}
                      dropdownOpen={openDropdown === i}
                      href={item.Href}
                      itemOrder={item.ItemOrder}
                      selected={i === idx}
                      title={item.Title}
                      index={i}
                      tooltipContent=""
                      onClick={() => {
                        setIdx(i);
                        if (item.subMenus && item.subMenus.length > 0) {
                          setOpenDropDown(openDropdown === i ? null : i);
                        }
                      }}
                    />
                    {item.subMenus && item.subMenus.length > 0 && openDropdown === i &&
                      <DropdownMenu
                        menuClassName={`z-50 absolute ${spEnv === "logp" ? "!bg-[#007167]" : "!bg-[#003d4c]"} pt-2 border-2 rounded-md`}
                        itemClassName="block px-4 py-2 text-base text-gray-700 hover:bg-gray-100"
                        items={item.subMenus}
                      />
                    }
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navigation;